import React, { useContext } from "react";
import { toast } from "@components/ui/use-toast";
import { FormWrapper } from "./FormWrapper";
import CustomSelect from "@components/common/CustomSelect";
import { LS_PRODUCT_TYPE } from "./allData";
import { BookOpen, Trash2, UploadCloud } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import InfoTooltip from "@components/common/InfoTooltip";
import { loadJsonData } from "@services/api/commonApis";
import { useQuery } from "react-query";
import PreviewLogo from "./PreviewLogo";
import Confirmation from "@components/common/ConfirmDialog";
import { GeneralInfoView } from "./GeneralInfoView";
import PluginLogoCropDialog from "./PluginLogoCropDialog";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import { GeneralContext } from "@services/providers/GeneralProvider";

type GeneralInfo = {
  pluginName: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: {
    minimum: string;
  };
  type: string;
  author: string;
  productType: string;
  ls_product_type: string[];
  pluginDependency: {
    name: string;
    id: string;
  }[];
  goToPage?: (index: number) => void;
  pluginLogo?: string;
  isNewPlugin: boolean;
};

type UserFormProps = GeneralInfo & {
  updateFields: (fields: Partial<GeneralInfo>) => void;
};

export function GeneralInfo({
  pluginName,
  version,
  tags,
  description1,
  description2,
  ls_version,
  type,
  author,
  productType,
  ls_product_type,
  pluginDependency,
  updateFields,
  goToPage,
  pluginLogo,
  isNewPlugin,
}: UserFormProps) {
  const { dispatch } = useMeshPluginContext();
  const [tagInput, setTagInput] = React.useState("");
  // Update parent component whenever tagInput changes
  React.useEffect(() => {
    updateFields({ tagInput } as any);
  }, [tagInput]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [previewLogo, setPreviewLogo] = React.useState<boolean>(false);
  const [logo, setLogo] = React.useState<string | null>(null);
  const [openDeleteLogoDialog, setOpenDeleteLogoDialog] =
    React.useState<boolean>(false);
  const [openCropDialog, setOpenCropDialog] = React.useState<boolean>(false);
  const [cropSourceImage, setCropSourceImage] = React.useState<string>("");

  const [isEditing, setIsEditing] = React.useState<boolean>(isNewPlugin);

  // Wrapper function to track unsaved changes
  const handleUpdateFields = (fields: Partial<GeneralInfo>) => {
    updateFields(fields);
    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_GENERAL_INFO,
      payload: true,
    });
  };
  const { isAdminUser } = useContext(GeneralContext);

  // Set default values based on user role
  React.useEffect(() => {
    if (isNewPlugin) {
      if (isAdminUser) {
        // For admin users, set type to "custom" if not already set
        if (!type) {
          updateFields({ type: "custom" });
        }
      } else {
        // For non-admin users, always set type to "custom" and ls_product_type to ["cmx"]
        updateFields({
          type: "custom",
          ls_product_type: ["cmx"],
        });
      }
    }
  }, [isAdminUser, isNewPlugin]);

  const { data: vendorproductdata } = useQuery(["loadJsonData"], () =>
    loadJsonData("/static/ls/json/device-vendor-product-mappingDict.json"),
  );

  // take all types from vendorproductdata keys
  const allTypes = vendorproductdata
    ? Object.keys(vendorproductdata)
        .map((key) => ({ label: key, value: key }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  // Function to handle adding tags
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && tagInput.trim() !== "") {
      e.preventDefault();

      if (!tags.includes(tagInput.trim())) {
        handleUpdateFields({ tags: [...tags, tagInput.trim()] });
      }

      setTagInput("");
    }
  };

  // Function to handle removing tags
  const removeTag = (tagToRemove: string) => {
    handleUpdateFields({ tags: tags.filter((tag) => tag !== tagToRemove) });
  };

  // Function to remove logo
  function removeLogo() {
    setOpenDeleteLogoDialog(true);
  }

  const onDelete = () => {
    handleUpdateFields({ pluginLogo: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setOpenDeleteLogoDialog(false);
  };

  // Function to preview logo
  const normalizedPluginDependency = Array.isArray(pluginDependency)
    ? pluginDependency
    : pluginDependency
      ? [pluginDependency]
      : [];

  // Function to handle preview logo
  const handlePluginDependencyChange = (value: string) => {
    handleUpdateFields({
      pluginDependency: [{ name: value, id: Date.now().toString() }],
    });
  };

  // Function to handle preview logo
  const handleTypeChange = (selectedOptions: string[]) => {
    handleUpdateFields({ ls_product_type: selectedOptions });
  };

  // Function to handle preview logo
  const handlePreviewLogo = () => {
    if (pluginLogo) {
      setPreviewLogo(true);
      setLogo(pluginLogo);
    }
  };

  const params = new URLSearchParams(window.location.search);
  const isEditMode = params.get("edit") === "1";

  return (
    <FormWrapper title="General Information" option="info" goToPage={goToPage}>
      {!isEditing ? (
        <GeneralInfoView
          pluginName={pluginName}
          version={version}
          tags={tags}
          description1={description1}
          description2={description2}
          ls_version={ls_version}
          type={type}
          author={author}
          productType={productType}
          ls_product_type={ls_product_type}
          pluginDependency={pluginDependency}
          pluginLogo={pluginLogo}
          onEdit={() => setIsEditing(true)}
          isNewPlugin={isNewPlugin}
        />
      ) : (
        <>
          <PreviewLogo
            open={previewLogo}
            handleClose={() => setPreviewLogo(false)}
            logo={logo || ""}
            onRecrop={() => {
              const currentLogo = logo || pluginLogo || "";
              if (!currentLogo) return;

              setCropSourceImage(currentLogo);
              setPreviewLogo(false);
              setOpenCropDialog(true);
            }}
          />

          <PluginLogoCropDialog
            open={openCropDialog}
            imageSrc={cropSourceImage}
            onClose={() => setOpenCropDialog(false)}
            onApply={(croppedLogo) => {
              handleUpdateFields({ pluginLogo: croppedLogo });
              setLogo(croppedLogo);
              setOpenCropDialog(false);
            }}
          />

          <Confirmation
            show={openDeleteLogoDialog}
            text="Are you sure you want to delete the logo?"
            confirmAction={onDelete}
            rejectAction={() => setOpenDeleteLogoDialog(false)}
            rejectText="Cancel"
          />

          <div className="grid grid-cols-2 gap-6 p-3 rounded-lg">
            <div className="grid cols-span-1 gap-2">
              <div>
                <label className="text-sm ml-1 ">Plugin Name *</label>
                <input
                  autoFocus
                  required
                  type="text"
                  value={pluginName}
                  placeholder="Plugin Name"
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm mt-1"
                  onChange={(e) =>
                    handleUpdateFields({ pluginName: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-sm ml-1 ">Plugin Version *</label>
                <input
                  required
                  type="text"
                  value={version}
                  placeholder="Enter version (eg. 1.0 , 1.0.0)"
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm mt-1"
                  onChange={(e) => {
                    // Filter out any characters that are not numbers or dots
                    const filtered = e.target.value.replace(/[^0-9.]/g, "");
                    handleUpdateFields({ version: filtered });
                  }}
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 ">Tags *</label>
                  <InfoTooltip text="Add relevant tags to categorize your plugin ( eg.'Action , Block IP '). Press Enter after typing each tag to add it to the list ." />
                </div>
                <div className="w-full p-2 rounded-xl border border-secondary bg-background text-sm mt-1">
                  <div className="flex flex-wrap gap-2 ">
                    {tags?.map((tag, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-secondary px-2 py-1 rounded-lg text-sm"
                      >
                        {tag}
                        <span
                          onClick={() => removeTag(tag)}
                          className="ml-2 cursor-pointer text-primary hover:text-muted-foreground font-bold"
                        >
                          ×
                        </span>
                      </div>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Enter tag and press Enter"
                    className="w-full bg-transparent focus:outline-none border-secondary mt-2"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 ">Plugin Description *</label>
                  <InfoTooltip text="Provide a brief overview of what the plugin does, showing for the users. Minimum length should be 15 characters for clarity and completeness." />
                </div>
                <textarea
                  required
                  value={description1}
                  placeholder="Description "
                  rows={4}
                  maxLength={250}
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm mt-1"
                  onChange={(e) =>
                    handleUpdateFields({ description1: e.target.value })
                  }
                />
                <div className="text-xs text-muted-foreground text-right">
                  {description1.length}/250 characters
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1">
                    Plugin Development Description *
                  </label>
                  <InfoTooltip text="Provide a brief overview of what the plugin does, showing for the creators. Minimum length should be 15 characters for clarity and completeness." />
                </div>
                <textarea
                  required
                  rows={4}
                  value={description2}
                  placeholder="Description 2"
                  maxLength={250}
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm mt-1"
                  onChange={(e) =>
                    handleUpdateFields({ description2: e.target.value })
                  }
                />
                <div className="text-xs text-muted-foreground text-right ">
                  {description2.length}/250 characters
                </div>
              </div>
            </div>

            <div className="grid cols-span-1 gap-2 mt-1">
              <div>
                <label className="text-sm ml-1 ">Author *</label>
                <input
                  required
                  type="text"
                  value={author}
                  placeholder="Author"
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm"
                  onChange={(e) =>
                    handleUpdateFields({ author: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm ml-1 mb-1">LS Version *</label>
                <Select
                  value={ls_version.minimum}
                  onValueChange={(value) =>
                    handleUpdateFields({ ls_version: { minimum: value } })
                  }
                >
                  <SelectTrigger className="w-full rounded-xl border border-secondary bg-background text-sm">
                    <SelectValue placeholder="Select LS Version" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectGroup>
                      <SelectItem value="4.7.7">4.7.7</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              {isAdminUser && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm mb-1">Type *</label>
                    <InfoTooltip text="Select the type of plugin whether it's a custom or generic plugin." />
                  </div>
                  <Select
                    onValueChange={(value) =>
                      handleUpdateFields({ type: value })
                    }
                    defaultValue={type}
                    value={type}
                  >
                    <SelectTrigger className="w-full rounded-xl border border-secondary bg-background text-sm">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="custom">Custom</SelectItem>
                        <SelectItem value="generic">Generic</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 mb-1">Product Type *</label>
                  <InfoTooltip text="Specify the type of product this plugin is associated with." />
                </div>
                <div className="border-secondary rounded-2xl overflow-hidden border bg-background">
                  <CustomSelect
                    data={allTypes}
                    placeholder="Select Product Type"
                    onSelect={(value) => {
                      handleUpdateFields({
                        productType: Array.isArray(value) ? value[0] : value,
                      });
                    }}
                    defaultValue={productType}
                    multiSelect={false}
                  />
                </div>
              </div>
              {isAdminUser && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm ml-1 mb-1">
                      LS Product Type *
                    </label>
                    <InfoTooltip text="Select the LinkShadow product types that this plugin is compatible with. You can select multiple options." />
                  </div>
                  <div className="border-secondary rounded-2xl overflow-hidden border bg-background">
                    <CustomSelect
                      data={LS_PRODUCT_TYPE}
                      placeholder="Select LS product type"
                      onSelect={handleTypeChange}
                      defaultValue={ls_product_type}
                      multiSelect
                    />
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm ml-1 mb-1">
                    Plugin Dependency Name{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <InfoTooltip text="Name of any base plugins which are required for this plugin to work." />
                </div>
                <input
                  type="text"
                  placeholder="Plugin Dependency Name"
                  className="w-full p-2 rounded-xl border border-secondary bg-background text-sm"
                  value={normalizedPluginDependency[0]?.name || ""}
                  onChange={(e) => {
                    handlePluginDependencyChange(e.target.value);
                  }}
                />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-xl border border-secondary bg-background p-4 shadow-sm">
                {/* Upload Section */}
                <div className="flex w-9/12 flex-col gap-2">
                  <label className="text-sm font-medium text-foreground">
                    Plugin Logo{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </label>

                  <label
                    htmlFor="plugin-logo"
                    className="group flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-secondary bg-muted/30 px-4 py-3 text-sm transition  hover:border-primary"
                  >
                    <UploadCloud
                      size={20}
                      className="text-muted-foreground transition group-hover:text-primary"
                    />

                    <span className="text-muted-foreground">
                      Click to {pluginLogo ? "update" : "upload"} Plugin Logo
                      {/* Plugin logo. */}
                      <span className="block text-xs group-hover:text-primary">
                        Accept .png, .jpg - up to 2MB.
                      </span>
                    </span>

                    <input
                      id="plugin-logo"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        const MAX_SIZE = 2 * 1024 * 1024;
                        if (file.size > MAX_SIZE) {
                          toast({
                            variant: "destructive",
                            title: "Error",
                            description:
                              "Image size exceeds 2MB. Please upload a smaller image.",
                            duration: 3000,
                          });
                          e.target.value = "";
                          return;
                        }

                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setCropSourceImage(reader.result as string);
                          setOpenCropDialog(true);
                          if (fileInputRef.current)
                            fileInputRef.current.value = "";
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                </div>

                {/* Preview */}
                <div
                  className={`group relative h-24 w-24 cursor-pointer overflow-hidden rounded-xl border shadow-sm transition ${pluginLogo && "hover:shadow-md"}`}
                  onClick={handlePreviewLogo}
                >
                  <img
                    src={`${pluginLogo || process.env.STATIC_PATH + "/images/plugin-default.jpg"}`}
                    alt="Plugin Logo Preview"
                    className={`h-full w-full object-cover transition duration-300 ${pluginLogo ? "group-hover:scale-105 group-hover:blur-sm" : "cursor-not-allowed"}`}
                  />

                  {pluginLogo && (
                    <div className="absolute inset-0 flex items-center justify-center bg-foregorund opacity-0 transition group-hover:opacity-100">
                      <span className="text-xs font-medium text-background">
                        View
                      </span>
                    </div>
                  )}

                  {pluginLogo && (
                    <Trash2
                      size={20}
                      className="
                    absolute right-1 top-1 rounded-full p-1
                    bg-transparent
                    text-background
                    opacity-0 shadow
                    transition
                    group-hover:opacity-100
                    group-hover:bg-foreground
                    hover:text-primary
                    "
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLogo();
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          {isEditMode && (
            <div className="flex justify-end text-primaryBlue mr-4">
              <p
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer border rounded-md py-2 px-3 border-muted-foreground hover:border-primaryBlue hover:text-primaryBlue transition-colors"
              >
                <BookOpen size={18} /> Show Summery
              </p>
            </div>
          )}
          {/* 
      <div className="flex justify-end">
        <Button
          onClick={handleSaveInfo}
          className="mt-4"
        >
          Save Genaral Information
        </Button>
      </div> */}
        </>
      )}
    </FormWrapper>
  );
}
